import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import SlideVisual from '@/components/study/SlideVisual';

/**
 * The chapter deck. Text on the left, a diagram the app draws on the right, and
 * every element arrives in sequence so the student watches the idea assemble
 * rather than reading a finished list. Curated gallery art for the chapter is
 * woven in as its own slide when an admin has approved any.
 */
export function SlideDeckViewer({ deck, subject }) {
  const slides = useMemo(() => {
    const base = deck?.slides || [];
    const art = (deck?.gallery || []).filter(g => g.file_url);
    if (!base.length || !art.length) return base;
    // Drop the gallery in after the opener, where a picture helps most.
    const at = Math.min(1, base.length);
    return [...base.slice(0, at), { kind: 'gallery', art }, ...base.slice(at)];
  }, [deck]);
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [deck]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx(i => Math.min(slides.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length]);

  const slide = slides[idx];
  if (!slide) return null;
  const hasVisual = !!slide.visual;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="slide-deck-viewer">
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-3xl flex flex-col justify-center bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-600 text-white p-5 sm:p-9 relative">
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
        <p className="relative font-heading text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/70">
          {subject} · slide {idx + 1} of {slides.length}
        </p>

        {slide.kind === 'gallery' ? (
          <div className="relative mt-3" data-testid="slide-gallery">
            <h3 className="font-display text-xl sm:text-3xl font-bold tracking-tight">Picture this chapter</h3>
            <p className="font-body text-white/80 text-[12px] sm:text-sm mt-1">
              Art our teachers made for this chapter — look, then read on.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {slide.art.slice(0, 4).map((a, i) => (
                <figure key={a.content_id} className="rounded-2xl overflow-hidden bg-white/10 border border-white/20 opacity-0 animate-slide-up"
                        style={{ animationDelay: `${120 + i * 110}ms` }}>
                  <img src={a.thumbnail_url || a.file_url} alt={a.title}
                       className="w-full h-40 sm:h-48 object-cover" loading="lazy" />
                  <figcaption className="px-3 py-2">
                    <p className="font-heading text-[11px] sm:text-[12px] text-white flex items-center gap-1.5">
                      <ImageIcon className="w-3 h-3" />{a.title}
                    </p>
                    {a.description && <p className="font-body text-[10px] text-white/70 leading-snug">{a.description}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        ) : (
          <div className={`relative mt-3 grid gap-5 sm:gap-7 ${hasVisual ? 'lg:grid-cols-[1.05fr,1fr] lg:items-start' : ''}`}>
            <div className="min-w-0">
              <h3 className="font-display text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                {slide.title}
              </h3>
              {slide.subtitle && (
                <p className="font-body text-white/85 mt-2 text-[13.5px] sm:text-base leading-snug">{slide.subtitle}</p>
              )}
              <ul className="mt-4 space-y-2">
                {(slide.bullets || []).map((b, i) => (
                  <li key={i} style={{ animationDelay: `${80 + i * 90}ms` }}
                      className="flex items-start gap-2.5 font-body text-[14px] sm:text-[16px] leading-relaxed text-white/95 opacity-0 animate-slide-up">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />{b}
                  </li>
                ))}
              </ul>
              {slide.takeaway && (
                <p className="mt-5 inline-block rounded-2xl bg-white/15 backdrop-blur-sm px-4 py-2 font-heading text-[11px] sm:text-sm">
                  Remember: {String(slide.takeaway).replace(/^\s*remember\s*[:\u2014-]\s*/i, '')}
                </p>
              )}
            </div>
            {hasVisual && (
              <div className="min-w-0 lg:border-l lg:border-white/15 lg:pl-7 self-start">
                <SlideVisual visual={slide.visual} replayKey={idx} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 flex-shrink-0">
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} data-testid="deck-prev"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-[12px] font-heading text-slate-700 disabled:opacity-40">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`deck-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-fuchsia-500' : 'w-1.5 bg-slate-300'}`} />
          ))}
        </div>
        <button onClick={() => setIdx(i => Math.min(slides.length - 1, i + 1))} disabled={idx >= slides.length - 1}
          data-testid="deck-next"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-[12px] font-heading disabled:opacity-40">
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default SlideDeckViewer;
